import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import { Plus, FileText, CopySimple, Trash, PencilSimple, Sparkle, ChartLineUp, Target, Clock, UploadSimple, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeGate from "@/components/UpgradeGate";
import { hasPlan } from "@/lib/planAccess";
import { listResumes, createResume, deleteResume, duplicateResume, uploadResumeForUpgrade, emptyResumeData, TEMPLATE_META } from "@/lib/resumeApi";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

export default function ResumeBuilder() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTplPicker, setShowTplPicker] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [targetRole, setTargetRole] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listResumes();
      setItems(data.items || []);
      setTemplates(data.templates || []);
    } catch (err) {
      console.error("list resumes failed", err);
      toast.error("Couldn't load your resumes");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (paid) load(); }, [load, paid]);

  const startCreate = useCallback(async (template) => {
    setCreating(true);
    try {
      const r = await createResume({ name: "Untitled Resume", template, data: emptyResumeData() });
      setShowTplPicker(false);
      nav(`/resume-builder/${r.id}`);
    } catch (err) {
      console.error("create failed", err);
      toast.error("Couldn't create resume");
    } finally { setCreating(false); }
  }, [nav]);


  const onUploadResume = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const allowed = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain"];
    const extOk = /\.(pdf|docx|txt)$/i.test(file.name);
    if (!extOk && !allowed.includes(file.type)) {
      toast.error("Please upload a PDF, DOCX or TXT resume");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Resume must be 10 MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const r = await uploadResumeForUpgrade(file, targetRole);
      toast.success("Resume upgraded for ATS");
      nav(`/resume-builder/${r.id}`);
    } catch (err) {
      console.error("resume import failed", err);
      toast.error(err.response?.data?.detail || "Couldn't upgrade that resume");
    } finally { setUploading(false); }
  }, [nav, targetRole]);

  const onDuplicate = useCallback(async (id) => {
    try { await duplicateResume(id); toast.success("Duplicated"); load(); }
    catch (err) { console.error("dup failed", err); toast.error(err.response?.data?.detail || "Couldn't duplicate"); }
  }, [load]);

  const onDelete = useCallback(async (id) => {
    if (!confirm("Delete this resume? This can't be undone.")) return;
    try { await deleteResume(id); toast.success("Deleted"); load(); }
    catch (err) { console.error("del failed", err); toast.error("Couldn't delete"); }
  }, [load]);

  const avgAts = items.length ? Math.round(items.reduce((s, r) => s + (r.ats_score || 0), 0) / items.length) : 0;
  const bestMatch = items.reduce((m, r) => Math.max(m, r.job_match_score || 0), 0);
  const lastUpdated = items[0]?.updated_at;

  if (!paid) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="resume-builder-page">
        <Navbar />
        <DashSidebar />
        <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-6xl mx-auto lg:mx-0">
          <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Resume Studio</div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium mb-6">Resume Builder</h1>
          <UpgradeGate title="Unlock Resume Builder" description="Create ATS-friendly resumes, use every Resume AI tool, analyze ATS/job matching, keep version history, and export your work with Pro or Premium." />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="resume-builder-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-6xl mx-auto lg:mx-0">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// AI Resume Builder</div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">Your resumes</h1>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">Build clean, single-column, ATS-friendly resumes — AI helps with content while you keep control of the facts.</p>
          </div>
          <button
            onClick={() => setShowTplPicker(true)}
            data-testid="new-resume-btn"
            className="self-start inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-lg transition-colors"
          >
            <Plus size={16} weight="bold" /> New resume
          </button>
        </div>

        {/* AI Resume Upgrade */}
        <section className="mb-8 rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-500/[0.10] via-[#0F0F12] to-[#0F0F12] p-5 sm:p-7" data-testid="rb-upload-upgrade">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-400/20 flex items-center justify-center shrink-0">
                <Sparkle size={24} weight="fill" className="text-blue-400" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-blue-400 font-semibold">Premium AI Resume Upgrade</div>
                <h2 className="text-xl sm:text-2xl font-medium mt-1">Upload your existing resume</h2>
                <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">Career GPS AI extracts your real information, improves wording, strengthens ATS compatibility and gives you a polished editable resume. Facts and numbers are never invented.</p>
                <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-zinc-500">
                  <span className="inline-flex items-center gap-1"><ShieldCheck size={13} className="text-emerald-400" /> Fact-preserving AI</span>
                  <span>PDF · DOCX · TXT</span>
                  <span>ATS-first</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-2 shrink-0">
              <input id="rb-upload-input" type="file" accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" onChange={onUploadResume} className="hidden" />
              <label htmlFor="rb-upload-input" className={`cursor-pointer inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-lg transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="rb-upload-btn">
                <UploadSimple size={17} weight="bold" /> {uploading ? "Analyzing…" : "Upload & Upgrade"}
              </label>
              <div className="flex items-center gap-2">
                <input value={targetRole} onChange={(e) => setTargetRole(e.target.value)} placeholder="Target role (optional)" className="w-full sm:w-52 bg-black/30 border border-white/10 rounded-lg px-3 py-3 text-sm outline-none focus:border-blue-500/50" />
                <ArrowRight size={17} className="hidden xl:block text-zinc-600" />
              </div>
            </div>
          </div>
        </section>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="resume-stats">
          <StatTile icon={FileText} label="Total resumes" value={items.length} color="text-blue-400" />
          <StatTile icon={ChartLineUp} label="Avg ATS score" value={avgAts || "—"} color="text-emerald-400" suffix={avgAts ? "/100" : ""} />
          <StatTile icon={Target} label="Best job match" value={bestMatch || "—"} color="text-amber-400" suffix={bestMatch ? "/100" : ""} />
          <StatTile icon={Clock} label="Last updated" value={fmtDate(lastUpdated)} color="text-zinc-300" small />
        </div>

        <div className="mb-8 bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 sm:p-5" data-testid="rb-ats-guidance">
          <div className="text-sm font-medium text-emerald-200">ATS-first by default</div>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">Exports use a single-column, text-based structure with standard headings and readable fonts. Keep important information out of graphics, tables, headers and footers.</p>
        </div>
        {loading && <div className="text-zinc-500" data-testid="rb-loading">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-10 text-center" data-testid="rb-empty">
            <FileText size={48} weight="duotone" className="text-blue-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium">Build your first AI resume</h3>
            <p className="text-zinc-500 mt-2 max-w-md mx-auto">Pick a template — we&apos;ll prefill your name and email, then AI helps you write a high-impact summary and bullet points.</p>
            <button
              onClick={() => setShowTplPicker(true)}
              data-testid="rb-empty-cta"
              className="mt-6 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-lg"
            >
              <Sparkle size={16} weight="fill" /> Start a resume
            </button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rb-grid">
            {items.map((r) => {
              const meta = TEMPLATE_META[r.template] || TEMPLATE_META.modern;
              return (
                <div key={r.id} className="bg-[#0F0F12] border border-white/10 rounded-xl p-5 hover:border-blue-500/30 transition-colors" data-testid={`rb-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link to={`/resume-builder/${r.id}`} className="text-base font-medium text-white hover:text-blue-300 block truncate" data-testid={`rb-open-${r.id}`}>
                        {r.name || "Untitled Resume"}
                      </Link>
                      <div className="text-xs text-zinc-500 mt-1 truncate">
                        <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: meta.accent }} />
                        {meta.label}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{fmtDate(r.updated_at)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-4 text-xs">
                    <span className="text-zinc-500">ATS</span>
                    <span className={`font-mono font-medium ${r.ats_score >= 80 ? "text-emerald-400" : r.ats_score >= 60 ? "text-blue-400" : "text-zinc-400"}`}>{r.ats_score || "—"}</span>
                    <span className="text-zinc-700">|</span>
                    <span className="text-zinc-500">Job match</span>
                    <span className={`font-mono font-medium ${r.job_match_score >= 80 ? "text-emerald-400" : r.job_match_score >= 60 ? "text-blue-400" : "text-zinc-400"}`}>{r.job_match_score || "—"}</span>
                  </div>
                  <div className="flex gap-2 mt-5">
                    <Link to={`/resume-builder/${r.id}`} data-testid={`rb-edit-${r.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-2 rounded-md">
                      <PencilSimple size={14} weight="bold" /> Edit
                    </Link>
                    <button onClick={() => onDuplicate(r.id)} data-testid={`rb-dup-${r.id}`} className="w-9 h-9 inline-flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-zinc-300" title="Duplicate">
                      <CopySimple size={14} />
                    </button>
                    <button onClick={() => onDelete(r.id)} data-testid={`rb-del-${r.id}`} className="w-9 h-9 inline-flex items-center justify-center bg-white/5 hover:bg-red-600/20 border border-white/10 hover:border-red-500/40 rounded-md text-zinc-400 hover:text-red-300 transition-colors" title="Delete">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Template picker modal */}
      {showTplPicker && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tpl-picker">
          <div className="bg-[#0F0F12] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold">// Pick a template</div>
                <h2 className="text-xl sm:text-2xl font-medium mt-1">Choose your starting point</h2>
              </div>
              <button onClick={() => setShowTplPicker(false)} className="text-zinc-400 hover:text-white" data-testid="tpl-picker-close">Close</button>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {templates.map((t) => {
                const meta = TEMPLATE_META[t.id] || TEMPLATE_META.modern;
                const premium = t.tier === "premium" || meta.tier === "premium";
                return (
                  <button
                    key={t.id}
                    disabled={creating}
                    onClick={() => startCreate(t.id)}
                    data-testid={`tpl-pick-${t.id}`}
                    className="group text-left bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 hover:border-white/30 rounded-2xl p-4 transition-all overflow-hidden"
                  >
                    <div className="h-24 rounded-xl border border-black/10 bg-white relative overflow-hidden mb-4 shadow-lg">
                      <div className="absolute inset-x-0 top-0 h-2" style={{background: meta.accent}} />
                      <div className="px-3 pt-4">
                        <div className="h-2.5 w-24 rounded bg-zinc-900/80" />
                        <div className="h-1.5 w-16 rounded mt-1.5" style={{background: meta.accent, opacity: .75}} />
                        <div className="h-px bg-zinc-200 mt-3" />
                        <div className="grid grid-cols-3 gap-1 mt-2"><span className="h-1 bg-zinc-200 rounded"/><span className="h-1 bg-zinc-200 rounded"/><span className="h-1 bg-zinc-200 rounded"/></div>
                        <div className="h-1 bg-zinc-100 rounded mt-1.5 w-11/12" /><div className="h-1 bg-zinc-100 rounded mt-1 w-9/12" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: meta.accent }} />
                        <div className="font-medium truncate">{t.label}</div>
                      </div>
                      {premium && <span className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 shrink-0">Premium</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{t.tagline}</p>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-400/80 mt-3">ATS-safe · Single column</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatTile = ({ icon: Icon, label, value, color, suffix = "", small = false }) => (
  <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-4">
    <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
      <Icon size={14} className={color} /> {label}
    </div>
    <div className={`${small ? "text-base sm:text-lg" : "text-2xl sm:text-3xl"} font-medium font-mono mt-2 ${color}`}>{value}{suffix && <span className="text-zinc-600 text-sm">{suffix}</span>}</div>
  </div>
);
